import React, { useImperativeHandle, useMemo } from "react";

import { FANumberInput } from "@impero-com/common/components/form/formAtomFields/faNumberInput";
import { FASelectInput } from "@impero-com/common/components/form/formAtomFields/faSelectInput";
import { FAStringInput } from "@impero-com/common/components/form/formAtomFields/faStringInput";
import { formAtom } from "@impero-com/common/utils/form/formAtom";
import {
    useFormAtom,
    useCompoundFormState,
} from "@impero-com/common/utils/form/formAtom/hooks";
import { FormAtom } from "@impero-com/common/utils/form/formAtom/types";
import {
    splitFormAtom,
    toSplitAtomValidationError,
} from "@impero-com/common/utils/form/splitFormAtom";
import { t } from "@impero-com/common/utils/i18n";
import { uuidRegEx } from "@impero-com/common/utils/uuid";
import { Button, FieldError, FormLabel, Svg } from "@impero-com/ui";
import { useAtom } from "jotai";
import { focusAtom } from "jotai-optics";
import * as Yup from "yup";

import { DataGridColumn } from "api/generated/model";
import DeleteIcon from "img/delete.svg";
import { generate } from "utils/guid";

import { CompoundFormForwardedRef } from "./fieldBuilder";
import * as theme from "./theme.css";

interface DataGridFieldTypeDetails {
    initialNumberOfRows: number;
    columns: DataGridColumn[];
}

interface DataGridEditorProps<FT extends DataGridFieldTypeDetails> {
    atom: FormAtom<FT>;
    maxNumberOfColumns: number;
    maxNumberOfRows: number;
    disabled?: boolean;
}

function useFieldTypeAtomColumns<FT extends DataGridFieldTypeDetails>(
    fieldTypeAtom: FormAtom<FT>,
    maxNumberOfColumns: number
) {
    const atoms = useMemo(() => {
        const columnsAtom: FormAtom<Array<DataGridColumn>> = formAtom(
            focusAtom(fieldTypeAtom, (fieldType) => fieldType.prop("columns")),
            Yup.array()
                .defined()
                .of(
                    Yup.object({
                        id: Yup.string()
                            .matches(uuidRegEx, {
                                name: "uuid",
                                message: t("Form_Error_Types_Regex", {
                                    field: "id",
                                    type: "UUID",
                                }),
                                excludeEmptyString: false,
                            })
                            .defined(),
                        title: Yup.string()
                            .defined()
                            .required(
                                t(
                                    "FieldBuilder_Error_DataGrid_Column_Name_Required"
                                )
                            ),
                        type: Yup.string().oneOf(["Text", "Numeric"]).defined(),
                    }).defined()
                )
                .min(1, t("FieldBuilder_Error_DataGrid_Columns_OneRequired"))
                .max(
                    maxNumberOfColumns,
                    t("FieldBuilder_Error_DataGrid_Columns_TooMany")
                )
                .test("unique-options", (value, { createError }) => {
                    const titleMap: Record<string, number[]> = {};
                    value.forEach((val, index) => {
                        const trimmedTitle = val.title.trim().toLowerCase();
                        if (titleMap[trimmedTitle]) {
                            titleMap[trimmedTitle].push(index);
                        } else {
                            titleMap[trimmedTitle] = [index];
                        }
                    });
                    const invalidIndices = Object.values(titleMap)
                        .filter((indices) => indices.length > 1)
                        .flatMap((indices) => indices);
                    if (invalidIndices.length > 0) {
                        return toSplitAtomValidationError(
                            createError({
                                message: t(
                                    "FieldBuilder_Error_DataGrid_ColumnAlreadyExists"
                                ),
                            }),
                            invalidIndices
                        );
                    }
                    return true;
                }),
            fieldTypeAtom
        );

        const columnsAtomsAtom = splitFormAtom(columnsAtom, (col) => col.id);
        return {
            columnsAtom,
            columnsAtomsAtom,
        };
    }, [fieldTypeAtom, maxNumberOfColumns]);
    return atoms;
}

function useFieldTypeAtomRows<FT extends DataGridFieldTypeDetails>(
    fieldTypeAtom: FormAtom<FT>,
    maxNumberOfRows: number
) {
    return useMemo(() => {
        const initialNumberOfRowsAtom = formAtom(
            focusAtom(fieldTypeAtom, (fieldType) =>
                fieldType.prop("initialNumberOfRows")
            ),
            Yup.number().defined().min(1).max(maxNumberOfRows),
            fieldTypeAtom
        );
        return initialNumberOfRowsAtom;
    }, [fieldTypeAtom, maxNumberOfRows]);
}

const DataGridEditorInner = <FT extends DataGridFieldTypeDetails>(
    {
        atom: fieldTypeAtom,
        maxNumberOfColumns,
        maxNumberOfRows,
        disabled = false,
    }: DataGridEditorProps<FT>,
    ref: CompoundFormForwardedRef
) => {
    const { columnsAtom, columnsAtomsAtom } = useFieldTypeAtomColumns(
        fieldTypeAtom,
        maxNumberOfColumns
    );

    const initialNumberOfRowsAtom = useFieldTypeAtomRows(
        fieldTypeAtom,
        maxNumberOfRows
    );

    const { error: columnsError } = useFormAtom(columnsAtom);
    const [columnAtoms, setColumnAtoms] = useAtom(columnsAtomsAtom);

    const allAtoms = useMemo(() => {
        return {
            columnsAtom,
            columnsAtomsAtom,
            initialNumberOfRowsAtom,
        };
    }, [columnsAtom, columnsAtomsAtom, initialNumberOfRowsAtom]);

    const { validate: validateForm } = useCompoundFormState(
        allAtoms,
        columnsAtom
    );
    useImperativeHandle(ref, () => {
        return {
            validate(force) {
                return validateForm(force);
            },
        };
    }, [validateForm]);

    const maxNumberOfColumnsReached = columnAtoms.length >= maxNumberOfColumns;

    return (
        <>
            <FormLabel>{t("FieldBuilder_Drawer_DataGrid_Columns")}</FormLabel>
            <div className={theme.fieldDataGridColumnsTable}>
                {columnAtoms.map((atom, index) => {
                    return (
                        <div
                            key={atom.toString()}
                            className={theme.fieldDataGridColumnRow}
                        >
                            <DataGridColumnEditor
                                atom={atom}
                                disabled={disabled}
                            />
                            {!disabled && (
                                <div>
                                    <Button
                                        id={`delete-column-link-${index}`}
                                        variant="link"
                                        className={theme.deleteTableRowButton}
                                        onClick={() =>
                                            void setColumnAtoms({
                                                type: "remove",
                                                atom,
                                            })
                                        }
                                    >
                                        <Svg
                                            src={DeleteIcon}
                                            className={theme.deleteIcon}
                                        />
                                    </Button>
                                </div>
                            )}
                        </div>
                    );
                })}
                <FieldError error={columnsError?.value} />
            </div>
            {!disabled && (
                <div className={theme.fieldDataGridColumnAddRow}>
                    <Button
                        id="add-column"
                        variant="dashed"
                        className={theme.fieldDataGridColumnAdd}
                        onClick={() =>
                            void setColumnAtoms({
                                type: "insert",
                                value: {
                                    id: generate(),
                                    title: "",
                                    type: "Text",
                                },
                            })
                        }
                        disabled={maxNumberOfColumnsReached}
                    >
                        {t("FieldBuilder_Drawer_DataGrid_Column_Add")}
                    </Button>
                    <div className={theme.tableAddPadder} />
                </div>
            )}
            <FANumberInput
                label={t("FieldBuilder_Drawer_DataGrid_Rows")}
                atom={initialNumberOfRowsAtom}
                disabled={disabled}
            />
        </>
    );
};

export const DataGridEditor = React.forwardRef(DataGridEditorInner);

const DataGridColumnEditor = ({
    atom,
    disabled = false,
}: {
    atom: FormAtom<DataGridColumn>;
    disabled?: boolean;
}) => {
    const [titleAtom, typeAtom] = useMemo(() => {
        return [
            formAtom(
                focusAtom(atom, (column) => column.prop("title")),
                Yup.reach(atom.schema, "title") as Yup.ISchema<string>,
                atom
            ),
            formAtom(
                focusAtom(atom, (column) => column.prop("type")),
                Yup.reach(atom.schema, "type") as Yup.ISchema<
                    "Text" | "Numeric"
                >,
                atom
            ),
        ];
    }, [atom]);

    const { error } = useFormAtom(atom);

    const options = useMemo(() => {
        return [
            {
                label: t("FieldBuilder_Drawer_DataGrid_Column_Type_Text"),
                value: "Text" as const,
            },
            {
                label: t("FieldBuilder_Drawer_DataGrid_Column_Type_Numeric"),
                value: "Numeric" as const,
            },
        ];
    }, []);
    return (
        <>
            <div className={theme.fieldDataGridColumnTitle}>
                <FAStringInput
                    atom={titleAtom}
                    disabled={disabled}
                    parentError={error}
                />
            </div>
            <div className={theme.fieldDataGridColumnType}>
                <FASelectInput
                    atom={typeAtom}
                    options={options}
                    disabled={disabled}
                />
            </div>
        </>
    );
};
